import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat';
import { compose } from 'redux';
import type { FormErrors, InjectedFormProps } from 'redux-form';
import { getFormSubmitErrors, getFormValues, reduxForm } from 'redux-form';

import type { W_WorkRequest_FindWorkRequestForEdit_VM } from 'ab-api/web/workRequest/findWorkRequestForEdit';

import { isValidTextInput } from '@acceligentllc/shared/utils/text';

import { DirectoryIcon } from '@acceligentllc/shared/enums/directoryIcon';
import { Priority } from '@acceligentllc/shared/enums/priority';
import { WorkRequestBidStatus } from '@acceligentllc/shared/enums/workRequestBidStatus';

import { EMAIL_REGEX } from '@acceligentllc/shared/constants/regex';

import type { RootState } from 'af-reducers';

import DirectoryNames from 'ab-enums/directoryNames.enum';

import { WORK_REQUEST_FORM } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import Breadcrumbs from 'af-components/Breadcrumbs';
import SubmitButton from 'af-components/SubmitButton';
import TabNavigation from 'af-components/TabNavigation';

import CLIENT from 'af-routes/client';

import * as AttachmentActions from 'af-actions/attachment';
import * as DivisionActions from 'af-actions/division';
import * as EmployeeActions from 'af-actions/employee';
import * as JobStatusActions from 'af-actions/jobStatus';
import * as LocationActions from 'af-actions/location';
import * as SaleActions from 'af-actions/sale';
import * as WorkRequestActions from 'af-actions/workRequests';

import WorkRequestUpsertFM from './formModel';
import styles from './styles.module.scss';
import { WorkRequestFormTabRoutes } from './types';
import WorkRequestCopyModal from '../Shared/WorkRequestCopyModal';

type Props = ConnectedProps<typeof connector> & InjectedFormProps<WorkRequestUpsertFM>;

interface Tab {
	id: number;
	label: string;
}

const TABS: Tab[] = [{ id: 0, label: 'Work Request' }, { id: 1, label: 'Job Hazard Assessment' }, { id: 2, label: 'Attachments' }];

const _initializeRootDirectories = () => {
	return Object.keys(DirectoryNames).map((_name, _index) => {
		const dirName = DirectoryNames[_name];
		const iconEnum = DirectoryIcon[_name];

		return {
			id: _index + 1,
			name: dirName,
			parentId: null,
			icon: iconEnum ?? null,
			lastModifiedAt: null,
			lastModifiedBy: null,
			attachmentIds: [],
		};
	});
};

const WorkRequestForm: React.FC<Props> = (props) => {
	const [currentlyEditedWorkRequest, setCurrentlyEditedWorkRequest] = React.useState<Nullable<W_WorkRequest_FindWorkRequestForEdit_VM>>(null);
	const [showCopyModal, setShowCopyModal] = React.useState(false);
	const {
		change,
		handleSubmit,
		createWorkRequest,
		editWorkRequest,
		companyName,
		initialize,
		findById,
		initialized,
		submitting,
		jobCode,
		title,
		customerCompanyName,
		priority,
		office,
		jobHazardAssessmentStatus,
		uploadingAttachments,
		uploadedAttachmentIds,
		deletePendingAttachments,
		billToEmail,
		jobCodeSubmitError,
		workLocation,
	} = props;

	const { workRequestId, '*': tabId } = useParams();
	const { state: { orgAlias } } = useLocation();
	const navigate = useNavigate();
	const uploadedAttachmentIdsToDeleteRef = React.useRef<Nullable<number[]>>(null);
	const cameFromPreviewTab = React.useRef<string | undefined>(undefined);

	const activeTabId = React.useMemo(() => {
		if (!tabId) {
			return 0;
		}

		switch (tabId) {
			case WorkRequestFormTabRoutes.DETAILS:
				return TABS[0].id;
			case WorkRequestFormTabRoutes.JOB_HAZARD_ASSESSMENT:
				return TABS[1].id;
			case WorkRequestFormTabRoutes.ATTACHMENTS:
				return TABS[2].id;
			default:
				return TABS[0].id;
		}
	}, [tabId]);
	const isEditMode = !!workRequestId;

	const clearPendingAttachments = React.useCallback(async () => {
		if (uploadedAttachmentIdsToDeleteRef.current?.length) {
			await deletePendingAttachments({ attachmentIds: uploadedAttachmentIdsToDeleteRef.current });
		}
	}, [deletePendingAttachments]);

	React.useEffect(() => {
		return () => {
			clearPendingAttachments();
		};
	}, [clearPendingAttachments]);

	React.useEffect(() => {
		uploadedAttachmentIdsToDeleteRef.current = uploadedAttachmentIds ?? null;
	}, [uploadedAttachmentIds]);

	const initializeForEditMode = React.useCallback(async () => {
		if (isEditMode && !initialized && !currentlyEditedWorkRequest) {
			cameFromPreviewTab.current = tabId;
			const wr = await findById(+workRequestId);
			if (wr.bidStatus === WorkRequestBidStatus.LOCKED_IN || wr.bidStatus === WorkRequestBidStatus.JOB) {
				console.error(`Cannot edit Work Request with bid status of ${wr.bidStatus}.`);
				navigate(CLIENT.ERROR.ERR404());
				return;
			}

			setCurrentlyEditedWorkRequest(wr);
			initialize(new WorkRequestUpsertFM(wr));
		} else if (!initialized) {
			initialize({
				estimateTotal: 0,
				priority: Priority.MEDIUM,
				numberOfEmployees: 0,
				directories: _initializeRootDirectories(),
			});
		}
	}, [currentlyEditedWorkRequest, findById, initialize, initialized, isEditMode, navigate, tabId, workRequestId]);

	const isWorkRequestTabValid =
		React.useMemo(() => {
			const isBillToEmailValid = billToEmail ? EMAIL_REGEX.test(billToEmail) : true;
			return (
				isValidTextInput(jobCode)
				&& isValidTextInput(title)
				&& isValidTextInput(customerCompanyName)
				&& !!priority
				&& !!office
				&& isBillToEmailValid
				&& !jobCodeSubmitError
			);
		}, [billToEmail, customerCompanyName, jobCode, jobCodeSubmitError, office, priority, title]);

	const areRequiredHazardAssessmentFieldsFilled = !!jobHazardAssessmentStatus;

	const renderTabLabel = React.useCallback((id: number) => {
		switch (id) {
			case TABS[0].id: {
				return (
					<div className={styles['work-request-form__tab']}>
						{!isWorkRequestTabValid && <span className="icon-warning" />}
						Work Request
					</div>
				) as React.JSX.Element;
			}
			case TABS[1].id: {

				return (
					<div className={styles['work-request-form__tab']}>
						{!areRequiredHazardAssessmentFieldsFilled && <span className="icon-warning" />}
						Job Hazard Assessment
					</div>
				) as React.JSX.Element;
			}
			case TABS[2].id: {
				return (
					<div className={styles['work-request-form__tab']}>
						Attachments
					</div>
				) as React.JSX.Element;
			}

			default: {
				return <></>;
			}
		}
	}, [areRequiredHazardAssessmentFieldsFilled, isWorkRequestTabValid]);

	React.useEffect(() => {
		initializeForEditMode();
	}, [initializeForEditMode]);

	const onSubmit = React.useCallback(async (form: WorkRequestUpsertFM) => {

		let createdWorkRequestId;
		// should be already checked by validation
		if (!form.jobCode
			|| !form.customerCompanyName
			|| !form.title
			|| !form.officeId
			|| !form.jobHazardAssessmentStatus
		) {
			return;
		}

		if (isEditMode && currentlyEditedWorkRequest) {
			const changedFields: Partial<WorkRequestUpsertFM> = {};
			for (const _key of Object.keys(currentlyEditedWorkRequest)) {
				if (currentlyEditedWorkRequest[_key] !== form[_key]) {
					changedFields[_key] = form[_key];
					if (_key === 'workRequestAttachments') {
						changedFields.directories = form.directories;
						changedFields.uploadedAttachmentIds = form.uploadedAttachmentIds;
					}
				}
			}

			const rm = WorkRequestUpsertFM.toEditRM(changedFields);
			await editWorkRequest(+workRequestId, rm);
		} else {
			const rm = WorkRequestUpsertFM.toCreateRM(form);
			const { id } = await createWorkRequest(rm);
			createdWorkRequestId = id;
		}

		navigate(CLIENT.COMPANY.WORK_REQUESTS.PREVIEW(orgAlias, companyName, createdWorkRequestId ?? workRequestId));
	}, [companyName, createWorkRequest, currentlyEditedWorkRequest, editWorkRequest, isEditMode, navigate, orgAlias, workRequestId]);

	const onTabSelect = React.useCallback((newTabId: number) => {
		let tabName = '';
		switch (newTabId) {
			case TABS[0].id:
				tabName = WorkRequestFormTabRoutes.DETAILS;
				break;
			case TABS[1].id:
				tabName = WorkRequestFormTabRoutes.JOB_HAZARD_ASSESSMENT;
				break;
			case TABS[2].id:
				tabName = WorkRequestFormTabRoutes.ATTACHMENTS;
				break;
			default:
				tabName = WorkRequestFormTabRoutes.DETAILS;
				break;
		}
		isEditMode
			? navigate(CLIENT.COMPANY.WORK_REQUESTS.EDIT_TAB(orgAlias, companyName, workRequestId, tabName))
			: navigate(CLIENT.COMPANY.WORK_REQUESTS.CREATE_TAB(orgAlias, companyName, tabName));
	}, [companyName, isEditMode, navigate, orgAlias, workRequestId]);

	const redirectToCopiedWorkRequestPage = React.useCallback((copiedWorkRequestId: number) => {
		navigate(CLIENT.COMPANY.WORK_REQUESTS.PREVIEW(orgAlias, companyName, `${copiedWorkRequestId}`));
	}, [companyName, navigate, orgAlias]);

	const openCopyModal = React.useCallback(() => setShowCopyModal(true), []);

	const closeCopyModal = React.useCallback(() => setShowCopyModal(false), []);

	const isUploadingAttachments = React.useMemo(() => !!uploadingAttachments && !!Object.keys(uploadingAttachments).length, [uploadingAttachments]);

	const breadcrumbItems = React.useMemo(() => {
		const items: { label: string; url?: string; }[] = [{ label: 'Work Request', url: CLIENT.COMPANY.WORK_REQUESTS.TABLE(orgAlias, companyName) }];

		if (isEditMode && currentlyEditedWorkRequest) {
			items.push({ label: currentlyEditedWorkRequest.jobCode });
			items.push({ label: 'Edit' });
		} else {
			items.push({ label: 'New Work Request' });
		}
		return items;
	}, [companyName, currentlyEditedWorkRequest, isEditMode, orgAlias]);

	const isSubmitDisabled = isUploadingAttachments || !isWorkRequestTabValid || !areRequiredHazardAssessmentFieldsFilled;

	const outletContext = React.useMemo(() => {
		return {
			change,
			formName: WORK_REQUEST_FORM,
			workRequestId,
			workLocation,
		};
	}, [change, workRequestId, workLocation]);

	const onBack = React.useCallback(() => {
		let redirectUrl = '';
		if (cameFromPreviewTab.current && workRequestId) {
			redirectUrl = CLIENT.COMPANY.WORK_REQUESTS.PREVIEW_TAB(orgAlias, companyName, workRequestId, cameFromPreviewTab.current);
		} else {
			redirectUrl = CLIENT.COMPANY.WORK_REQUESTS.TABLE(orgAlias, companyName);
		}
		navigate(redirectUrl);
	}, [companyName, workRequestId, navigate, orgAlias]);

	if (isEditMode && !currentlyEditedWorkRequest) {
		return (
			<Breadcrumbs items={[{ label: 'Loading' }]} />
		);
	}

	return (
		<>
			<Breadcrumbs items={breadcrumbItems} />
			<div className={styles['work-request-form']}>
				<div className={styles['work-request-form__submit-section']}>
					<div className={styles['work-request-form__submit-section__hint']}>
						Please Fill out both Work Request and Job Hazard Assessment forms to submit.
					</div>
					<Button
						label="Cancel"
						onClick={onBack}
						style="secondary"
					/>
					{isEditMode &&
						<Button
							label="Copy"
							onClick={openCopyModal}
							style="secondary"
						/>
					}
					<SubmitButton
						disabled={isSubmitDisabled}
						label={isEditMode ? 'Save' : 'Submit'}
						onClick={handleSubmit(onSubmit)}
						reduxFormSubmitting={submitting}
						variant="primary"
					/>
				</div>
				<TabNavigation
					active={activeTabId}
					className={styles['work-request-form__tabs-navigation']}
					navigationClassName={styles['work-request-form__tabs-navigation']}
					onClick={onTabSelect}
					renderLabel={renderTabLabel}
					tabs={TABS}
				/>
				<Outlet context={outletContext} />
				{(currentlyEditedWorkRequest && workRequestId) &&
					<WorkRequestCopyModal
						closeModal={closeCopyModal}
						currentWorkRequestCode={currentlyEditedWorkRequest.jobCode}
						currentWorkRequestId={+workRequestId}
						redirectToCopiedWorkRequestPage={redirectToCopiedWorkRequestPage}
						showModal={showCopyModal}
					/>
				}
			</div>
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;
	const formState = getFormValues(WORK_REQUEST_FORM);

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const {
		jobCode,
		title,
		customerCompanyName,
		priority,
		office,
		jobHazardAssessmentStatus,
		uploadingAttachments,
		uploadedAttachmentIds,
		billToEmail,
		workLocation,
	} = formState(state) as WorkRequestUpsertFM ?? ({} as Partial<WorkRequestUpsertFM>);

	const getSubmitErrors = getFormSubmitErrors(WORK_REQUEST_FORM);

	const { jobCode: jobCodeSubmitError } = getSubmitErrors(state) as FormErrors<WorkRequestUpsertFM, string>;

	return {
		companyName: companyData.name,
		jobCode,
		title,
		customerCompanyName,
		priority,
		office,
		jobHazardAssessmentStatus,
		uploadingAttachments,
		uploadedAttachmentIds,
		billToEmail,
		jobCodeSubmitError,
		workLocation,
	};
}

function mapDispatchToProps() {
	return {
		createWorkRequest: WorkRequestActions.create,
		deletePendingAttachments: AttachmentActions.deletePendingAttachments,
		editWorkRequest: WorkRequestActions.edit,
		findAllDivisions: DivisionActions.findAllForCompany,
		findAllLocations: LocationActions.findList,
		findAllProjectManagers: EmployeeActions.findAllProjectManagers,
		findAllSuperintendentsAndProjectManagers: EmployeeActions.findAllSuperintendentsAndProjectManagers,
		findById: WorkRequestActions.findByIdForEdit,
		findJobStatuses: JobStatusActions.findList,
		findSales: SaleActions.findAllForForm,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType>(
	connector,
	reduxForm({
		form: WORK_REQUEST_FORM,
		validate: WorkRequestUpsertFM.validate,
		warn: WorkRequestUpsertFM.warn,
		shouldError: () => true, // show errors even when switching tabs
	}),
	React.memo
);

export default enhance(WorkRequestForm);
